/** @format */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from "react-router-dom";
import { useAuth } from '../contexts/AuthContext';
import { useCourses } from '../hooks/useCourses';
import { useCart } from '../contexts/CartContext';
import { Course } from '../types';
import { CartItem } from '../types';
import CourseDetailsModal from '../components/CourseDetailsModal';
import EditCourseModal from '../components/EditCourseModal';
import CatalogCourseCard from '../components/CatalogCourseCard';
import CustomDropdown from '../components/CustomDropdown';
import {
  AcademicCapIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import AIChatButton from '../components/AIChatButton';
import { shouldShowIOSWebViewAlert } from '../utils/deviceDetection';

const CourseCatalog: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { addToCart, cartItems, removeFromCart, clearCart } = useCart();
  const { courses, loading, error, loadMoreCourses, hasMore, fetchCourses } =
    useCourses();



  // Track if we've already fetched courses to prevent unnecessary re-fetches
  const hasFetchedCourses = useRef(false);
  const previousProgram = useRef<string | undefined>(undefined);

  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [showCourseModal, setShowCourseModal] = useState(false);
  const [editingCourse, setEditingCourse] = useState<Course | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [favorites, setFavorites] = useState<Set<string>>(new Set());

  // Filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedProgram, setSelectedProgram] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");

  const [selectedPrice, setSelectedPrice] = useState("");
  const [selectedLanguage, setSelectedLanguage] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [coursesPerPage] = useState(6);

  // Determine user role
  const normalizedRole = (currentUser?.role || '').toString().trim().toLowerCase();
  const isAdmin = normalizedRole === 'admin';
  const isAuthor = normalizedRole === 'author'; // Allow authors regardless of status to see their courses

  // Load data on mount
  useEffect(() => {
    // Only fetch once on mount
    if (!hasFetchedCourses.current) {
      if (currentUser?.program) {
        // Filter courses by user's program
        fetchCourses({ userProgram: currentUser.program });
        previousProgram.current = currentUser.program;
      } else {
        // Load all courses if no program is set
        fetchCourses();
        previousProgram.current = undefined;
      }
      hasFetchedCourses.current = true;
    }
  }, [currentUser?.program]); // Remove fetchCourses from dependency

  // Handle program changes
  useEffect(() => {
    // Only refetch if program actually changed and we've already fetched courses
    if (hasFetchedCourses.current && 
        currentUser?.program && 
        currentUser.program !== previousProgram.current) {
      fetchCourses({ userProgram: currentUser.program });
      previousProgram.current = currentUser.program;
    }
  }, [currentUser?.program]); // Remove fetchCourses from dependency

  // Reset selected category when program changes
  useEffect(() => {
    setSelectedCategory("");
  }, [selectedProgram]);


  // Get filtered categories based on program
  const getFilteredCategories = () => {
    if (!selectedProgram) {
      // Get unique categories from all courses
      const allCategories = Array.from(
        new Set(courses.map((course) => course.cat_id).filter(Boolean))
      ).map(catName => ({ id: catName, name: catName }));
      return allCategories;
    }

    // Get unique category names from courses that have the selected program
    const relevantCourses = courses.filter((course) => {
      if (!course.program) {
        return false;
      }
      
      // Simple exact match - if course contains the program, display it
      let matches = false;
      if (Array.isArray(course.program)) {
        matches = course.program.includes(selectedProgram);
      } else {
        matches = course.program === selectedProgram;
      }
      
      return matches;
    });

    const categoryNames = Array.from(
      new Set(relevantCourses.map((course) => course.cat_id).filter(Boolean))
    );

    return categoryNames.map(catName => ({ id: catName, name: catName }));
  };

  // Get unique languages
  const uniqueLanguages = Array.from(
    new Set(courses.map((course) => course.language).filter(Boolean))
  );

  // Filter courses
  const filteredCourses = courses.filter((course) => {
    // Don't display draft courses
    if (course.status === "draft") {
      return false;
    }
    
    if (
      searchTerm &&
      !course.name.toLowerCase().includes(searchTerm.toLowerCase())
    )
      return false;
    if (selectedProgram) {
      if (!course.program) {
        return false;
      }
      
      // Simple exact match - if course contains the program, display it
      let programMatches = false;
      if (Array.isArray(course.program)) {
        programMatches = course.program.includes(selectedProgram);
      } else {
        programMatches = course.program === selectedProgram;
      }
      
      if (!programMatches) {
        return false;
      }
    }
    if (selectedCategory && course.cat_id !== selectedCategory) return false;

    if (
      selectedPrice === "free" &&
      course.pricee !== "0" &&
      course.pricee !== "free"
    )
      return false;
    if (
      selectedPrice === "paid" &&
      (course.pricee === "0" || course.pricee === "free")
    )
      return false;
    if (selectedLanguage && course.language !== selectedLanguage) return false;
    return true;
  });

  // Get paginated courses
  const getPaginatedCourses = () => {
    const startIndex = (currentPage - 1) * coursesPerPage;
    const endIndex = startIndex + coursesPerPage;
    return filteredCourses.slice(startIndex, endIndex);
  };

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedProgram, selectedCategory, selectedPrice, selectedLanguage]);

  // Cart functions
  const handleAddToCart = (course: Course) => {
    const cartItem = {
      type: "course" as const,
      itemId: course.id,
      name: course.name,
      price:
        course.pricee === "0" || course.pricee === "free"
          ? 0
          : parseFloat(course.pricee),
      image: course.image_url,
    };
    addToCart(cartItem);
  };

  const cartTotal = cartItems.reduce((total: number, item: CartItem) => {
    return total + item.price;
  }, 0);

  const clearFilters = () => {
    setSearchTerm("");
    // Don't reset program filter if user has a program set
    if (!currentUser?.program) {
      setSelectedProgram("");
    }
    setSelectedCategory("");

    setSelectedPrice("");
    setSelectedLanguage("");
  };

  const refreshCourses = () => {
    if (currentUser?.program) {
      fetchCourses({ userProgram: currentUser.program });
    } else {
      fetchCourses();
    }
  };

  const handleCourseClick = (course: Course) => {
    setSelectedCourse(course);
    setShowCourseModal(true);
  };

  // Handle edit course
  const handleEditCourse = (course: Course) => {
    setEditingCourse(course);
    setShowEditModal(true);
  };

  // Handle edit modal close
  const handleEditModalClose = () => {
    setShowEditModal(false);
    setEditingCourse(null);
  };

  const closeCourseModal = () => {
    setShowCourseModal(false);
    setSelectedCourse(null);
  };

  // Pagination functions
  const goToPage = (page: number) => {
    setCurrentPage(page);
  };

  const nextPage = () => {
    const totalPages = Math.ceil(filteredCourses.length / coursesPerPage);
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 overflow-x-hidden">
      {/* Hero Section */}
      <section className="hero-section relative bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 overflow-hidden">
        <div className="relative z-10 w-full max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-6 sm:py-8 lg:py-12" style={{ width: '100%', maxWidth: '100vw' }}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-8 items-center" style={{ width: '100%' }}>
            {/* Left - Text */}
            <div className="text-center lg:text-left space-y-3 sm:space-y-4" style={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
              {/* Badge */}
              <div className="inline-flex items-center px-3 py-1.5 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full text-white text-xs font-medium">
                <AcademicCapIcon className="h-3 w-3 mr-1.5 text-blue-300" />
                Course Catalog
              </div>

              {/* Title */}
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight text-white">
                <span className="block">Find Your Next Course</span>
                <span className="block bg-gradient-to-r from-blue-200 via-cyan-200 to-indigo-200 bg-clip-text text-transparent">
                  Learn. Progress. Achieve.
                </span>
              </h1>

              {/* Subtitle */}
              <p className="text-base sm:text-lg text-blue-100 leading-relaxed max-w-2xl mx-auto lg:mx-0">
                Explore expert-led courses tailored for your program and goals.
              </p>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 justify-center lg:justify-start" style={{ width: '100%', maxWidth: '100%' }}>
                <a
                  href="#catalog-search"
                  className="inline-flex items-center justify-center px-3 sm:px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/20 text-white font-semibold rounded-lg hover:bg-white/20 transition-all duration-200 text-sm"
                >
                  <MagnifyingGlassIcon className="h-4 w-4 mr-1.5" /> Start Searching
                </a>
              </div>
            </div>

            {/* Right - Visuals (desktop only) */}
            <div className="relative hidden xl:block">
              <div className="space-y-4">
                <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-cyan-500 rounded-full flex items-center justify-center">
                      <AcademicCapIcon className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold">Expert-Led</h3>
                      <p className="text-blue-200 text-sm">High-quality content</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 ml-8">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-indigo-400 to-purple-500 rounded-full flex items-center justify-center">
                      <MagnifyingGlassIcon className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold">Smart Filters</h3>
                      <p className="text-blue-200 text-sm">Find the right course fast</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Wave Divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg className="w-full h-8 sm:h-12 text-gray-50" viewBox="0 0 1200 120" preserveAspectRatio="none">
            <path d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z" opacity=".25" fill="currentColor"></path>
            <path d="M0,0V15.81C13,36.92,27.64,56.86,47.69,72.05,99.41,111.27,165,111,224.58,91.58c31.15-10.15,60.09-26.07,89.67-39.8,40.92-19,84.73-46,130.83-49.67,36.26-2.85,70.9,9.42,98.6,31.56,31.77,25.39,62.32,62,103.63,73,40.44,10.79,81.35-6.69,119.13-24.28s75.16-39,116.92-43.05c59.73-5.85,113.28,22.88,168.9,38.84,30.2,8.66,59,6.17,87.09-7.5,22.43-10.89,48-26.93,60.65-49.24V0Z" opacity=".5" fill="currentColor"></path>
            <path d="M0,0V5.63C149.93,59,314.09,71.32,475.83,42.57c43-7.64,84.23-20.12,127.61-26.46,59-8.63,112.48,12.24,165.56,35.4C827.93,77.22,886,95.24,951.2,90c86.53-7,172.46-45.71,248.8-84.81V0Z" fill="currentColor"></path>
          </svg>
        </div>
      </section>

      {/* Search and Controls */}
      <div className="bg-white border-b border-gray-100">
        <div id="catalog-search" className="w-full max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            {/* Title and Search */}
            <div className="flex-1">
              <div className="relative max-w-md">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search courses..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm("")}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                )}
              </div>
            </div>

            {/* Filter Toggle and Cart */}
            <div className="flex items-center gap-4">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-all duration-200 text-base"
              >
                <FunnelIcon className="h-5 w-5" />
                <span>Filters</span>
              </button>

            </div>
          </div>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="bg-white border-b border-gray-100 shadow-sm">
          <div className="w-full max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
              {/* Program Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Program
                  {currentUser?.program && (
                    <span className="text-xs text-gray-500 ml-2">
                      (Filtered by {currentUser.program})
                    </span>
                  )}
                </label>
                <CustomDropdown
                  value={selectedProgram}
                  onChange={setSelectedProgram}
                  options={[
                    { 
                      value: "", 
                      label: currentUser?.program ? `${currentUser.program} Only` : "All Programs" 
                    },
                    ...(currentUser?.program ? [] : [
                      { value: "ICAG", label: "ICAG" },
                      { value: "CITG", label: "CITG" }
                    ])
                  ]}
                  placeholder="All Programs"
                  className="w-full"
                  disabled={!!currentUser?.program}
                />
              </div>

              {/* Category Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category
                </label>
                <CustomDropdown
                  value={selectedCategory}
                  onChange={setSelectedCategory}
                  options={[
                    { value: "", label: "All Categories" },
                    ...getFilteredCategories().map((category) => ({
                      value: category.id,
                      label: category.name
                    }))
                  ]}
                  placeholder="All Categories"
                  className="w-full"
                />
              </div>

              {/* Price Filter - Hidden on iOS WebView */}
              {!shouldShowIOSWebViewAlert() && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Price
                  </label>
                  <CustomDropdown
                    value={selectedPrice}
                    onChange={setSelectedPrice}
                    options={[
                      { value: "", label: "All Prices" },
                      { value: "free", label: "Free" },
                      { value: "paid", label: "Paid" }
                    ]}
                    placeholder="All Prices"
                    className="w-full"
                  />
                </div>
              )}

              {/* Language Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Language
                </label>
                <CustomDropdown
                  value={selectedLanguage}
                  onChange={setSelectedLanguage}
                  options={[
                    { value: "", label: "All Languages" },
                    ...uniqueLanguages.map((language) => ({
                      value: language,
                      label: language
                    }))
                  ]}
                  placeholder="All Languages"
                  className="w-full"
                />
              </div>



              {/* Clear Filters */}
              <div className="flex items-end">
                <button
                  onClick={clearFilters}
                  className="w-full px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all duration-200"
                >
                  Clear All
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="w-full max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-4">
        {/* Results Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {filteredCourses.length}{" "}
              {filteredCourses.length === 1 ? "Course" : "Courses"} Found
            </h2>
            {currentUser?.program && (
              <p className="text-sm text-blue-600 mt-1">
                Showing {currentUser.program} program courses
              </p>
            )}
            {Object.values({
              searchTerm,
              selectedProgram,
              selectedCategory,
              selectedPrice,
              selectedLanguage,
            }).some(Boolean) && (
              <p className="text-sm text-gray-500 mt-1">
                Additional filters applied
              </p>
            )}

          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* Courses List */}
        {loading ? (
          <div className="flex justify-center items-center min-h-[400px]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-lg text-gray-600 font-medium">Loading courses...</p>
            </div>
          </div>
        ) : filteredCourses.length > 0 ? (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {getPaginatedCourses().map((course) => (
                <CatalogCourseCard
                  key={course.id}
                  course={course}
                  onClick={() => handleCourseClick(course)}
                  onAddToCart={handleAddToCart}
                  showEditButton={isAdmin || (isAuthor && course.userId === currentUser?.uid)}
                  onEdit={() => handleEditCourse(course)}
                />
              ))}
            </div>

            {/* Pagination Controls */}
            {filteredCourses.length > coursesPerPage && (
              <div className="flex items-center justify-center mt-8 space-x-2">
                {/* Previous Page Button */}
                <button
                  onClick={prevPage}
                  disabled={currentPage === 1}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    currentPage === 1
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200 hover:border-gray-300'
                  }`}
                  aria-label="Previous page"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>

                {/* Page Numbers */}
                {(() => {
                  const totalPages = Math.ceil(filteredCourses.length / coursesPerPage);
                  const maxVisiblePages = 5;
                  let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
                  let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
                  
                  if (endPage - startPage + 1 < maxVisiblePages) {
                    startPage = Math.max(1, endPage - maxVisiblePages + 1);
                  }

                  const pages = [];
                  for (let i = startPage; i <= endPage; i++) {
                    pages.push(i);
                  }

                  return pages.map(page => (
                    <button
                      key={page}
                      onClick={() => goToPage(page)}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        page === currentPage
                          ? 'bg-primary-600 text-white'
                          : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200 hover:border-gray-300'
                      }`}
                      aria-label={`Go to page ${page}`}
                    >
                      {page}
                    </button>
                  ));
                })()}

                {/* Next Page Button */}
                <button
                  onClick={nextPage}
                  disabled={currentPage === Math.ceil(filteredCourses.length / coursesPerPage)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    currentPage === Math.ceil(filteredCourses.length / coursesPerPage)
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200 hover:border-gray-300'
                  }`}
                  aria-label="Next page"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            )}

            {/* Page Info */}
            <div className="text-center mt-4 text-sm text-gray-600">
              Showing {((currentPage - 1) * coursesPerPage) + 1} to {Math.min(currentPage * coursesPerPage, filteredCourses.length)} of {filteredCourses.length} courses
            </div>
          </>
        ) : (
          <div className="text-center py-12">
            <AcademicCapIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No courses found
            </h3>
            <p className="text-gray-500 mb-4">
              {currentUser?.program
                ? `No ${currentUser.program} courses available at the moment. Try adjusting your search or filters.`
                : "Try adjusting your search or filters to find what you're looking for."}
            </p>
            <button
              onClick={clearFilters}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Clear All Filters
            </button>
          </div>
        )}

        {/* Load More Button - Only show if there are more courses than can fit on one page */}
        {hasMore && filteredCourses.length > coursesPerPage && (
          <div className="text-center mt-8">
            <button
              onClick={loadMoreCourses}
              disabled={loading}
              className="px-6 py-3 bg-white border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Loading..." : "Load More Courses"}
            </button>
          </div>
        )}
      </div>

      {/* Course Details Modal */}
      {selectedCourse && (
        <CourseDetailsModal
          isOpen={showCourseModal}
          onClose={closeCourseModal}
          course={selectedCourse}
          onAddToCart={handleAddToCart}
          onToggleFavorite={() => {
            setFavorites((prev) => {
              const newFavorites = new Set(prev);
              if (newFavorites.has(selectedCourse.id)) {
                newFavorites.delete(selectedCourse.id);
              } else {
                newFavorites.add(selectedCourse.id);
              }
              return newFavorites;
            });
          }}
          isFavorite={favorites.has(selectedCourse.id)}
          isInCart={cartItems.some(item => item.type === 'course' && item.itemId === selectedCourse.id)}
        />
      )}

      {/* Edit Course Modal */}
      <EditCourseModal
        isOpen={showEditModal}
        onClose={handleEditModalClose}
        course={editingCourse}
      />

      {/* AI Chat Button */}
      <AIChatButton />
    </div>
  );
};

export default CourseCatalog;
